import isNil from 'lodash/isNil';

import { SurveyAttribute, SurveysFormValues } from '@occc-app/routes/Audience/types';
import {
  Audience_Survey_Response_Operator,
  AudienceSurveyResponseTargetingAttributeInput,
  AudienceTargetingAttributesInput,
  GeneralSurveyFragment,
} from '@occc/graphql';

import {
  MemberResponded,
  SingleOrMultipleChoiceSurveyQuestion,
  SingleOrMultipleChoiceSurveysFormValues,
  SliderSurveyQuestion,
  SliderSurveysFormValues,
} from './types';
import {
  inputOperatorBySliderOperator,
  sliderOperatorByInputOperator,
  titleBySliderOperator,
} from './constants';
import { RESPONDED_WITH_ANY_OF_LABEL, RESPONDED_WITH_LABEL } from './copies';

export const getSurveyQuestion = <Q extends GeneralSurveyFragment['questions'][0]>(
  survey: Omit<GeneralSurveyFragment, 'questions'> & { questions: Q[] },
) => {
  return survey.questions[0];
};

export const getSurveysFieldName = (prefix: string, fieldName: keyof SurveysFormValues) =>
  `${prefix}.${fieldName}`;

export const getIsSingleOrMultipleChoiceQuestion = (
  surveyQuestion: GeneralSurveyFragment['questions'][0],
): surveyQuestion is SingleOrMultipleChoiceSurveyQuestion => {
  return (
    surveyQuestion?.__typename === 'SurveyQuestionSingleChoice' ||
    surveyQuestion?.__typename === 'SurveyQuestionMultipleChoice'
  );
};

export const getIsSingleOrMultipleChoiceQuestionResponded = (
  formValues: SurveysFormValues,
): formValues is SingleOrMultipleChoiceSurveysFormValues => {
  const { survey, respondedStatus } = formValues;
  if (!survey) {
    return false;
  }

  const surveyQuestion = getSurveyQuestion(survey);
  const isSingleOrMultipleChoiceQuestion = getIsSingleOrMultipleChoiceQuestion(surveyQuestion);
  return isSingleOrMultipleChoiceQuestion && respondedStatus === 'responded';
};

export const getIsSliderQuestion = (
  surveyQuestion: GeneralSurveyFragment['questions'][0],
): surveyQuestion is SliderSurveyQuestion => {
  return surveyQuestion?.__typename === 'SurveyQuestionIntSlider';
};

export const getIsSliderQuestionResponded = (
  formValues: SurveysFormValues,
): formValues is SliderSurveysFormValues => {
  const { survey, respondedStatus } = formValues;
  if (!survey) {
    return false;
  }

  const surveyQuestion = getSurveyQuestion(survey);
  const isSliderQuestion = getIsSliderQuestion(surveyQuestion);

  return isSliderQuestion && respondedStatus === 'responded';
};

export const getIsSliderQuestionRespondedAndBetweenOperator = (
  formValues: SurveysFormValues,
): formValues is SliderSurveysFormValues => {
  const { sliderOperator } = formValues;
  const isSliderQuestionSelected = getIsSliderQuestionResponded(formValues);

  return isSliderQuestionSelected && sliderOperator === 'is-between';
};

export const getIsSurveysSectionFilled = (formValues: SurveysFormValues | undefined): boolean => {
  if (!formValues) {
    return false;
  }

  const { respondedStatus, survey, surveyAnswers, sliderOperator, minValue, maxValue, value } =
    formValues;

  if (respondedStatus === 'not-responded') {
    return !!survey;
  }

  if (!survey) {
    return false;
  }

  const surveyQuestion = getSurveyQuestion(survey);

  if (surveyAnswers && getIsSingleOrMultipleChoiceQuestion(surveyQuestion)) {
    return surveyAnswers.length > 0;
  }

  const isSliderQuestion = getIsSliderQuestion(surveyQuestion);

  if (isSliderQuestion && sliderOperator === 'is-between') {
    return !isNil(minValue) && !isNil(maxValue);
  }

  if (isSliderQuestion && sliderOperator !== 'is-between') {
    return !isNil(value);
  }

  return false;
};

export const getSurveyResponseTargetingAttributes = (
  surveyItems: SurveysFormValues[],
): AudienceTargetingAttributesInput['surveyResponseTargetingAttribute'] => {
  return surveyItems
    .map((formValues): AudienceSurveyResponseTargetingAttributeInput | null => {
      const {
        respondedStatus,
        survey,
        surveyAnswers,
        sliderOperator,
        minValue,
        maxValue,
        value,
        locations,
        locationGroups,
      } = formValues;

      if (!survey) {
        return null;
      }

      const surveyQuestion = getSurveyQuestion(survey);

      if (respondedStatus === 'not-responded') {
        return {
          surveyUuid: survey.uuid,
          operator: Audience_Survey_Response_Operator.IsNotIn,
          questionUuid: surveyQuestion.uuid,
          selectedOptions: [],
        };
      }

      const isSingleOrMultipleChoiceQuestion = getIsSingleOrMultipleChoiceQuestion(surveyQuestion);
      const locationsUuids = (locations || []).map(({ uuid }) => uuid);
      const locationGroupUuids = (locationGroups || []).map(({ uuid }) => uuid);
      if (isSingleOrMultipleChoiceQuestion) {
        return {
          surveyUuid: survey.uuid,
          operator: Audience_Survey_Response_Operator.IsIn,
          questionUuid: surveyQuestion.uuid,
          selectedOptions: surveyAnswers,
          locationsUuids,
          locationGroupUuids,
        };
      }

      const isSliderQuestion = getIsSliderQuestion(surveyQuestion);
      if (isSliderQuestion && sliderOperator === 'is-between') {
        return {
          surveyUuid: survey.uuid,
          operator: Audience_Survey_Response_Operator.IsBetween,
          questionUuid: surveyQuestion.uuid,
          minAmount: minValue,
          maxAmount: maxValue,
          locationsUuids,
          locationGroupUuids,
        };
      }

      if (isSliderQuestion && sliderOperator !== 'is-between' && !isNil(value)) {
        return {
          surveyUuid: survey.uuid,
          operator: inputOperatorBySliderOperator[sliderOperator],
          questionUuid: surveyQuestion.uuid,
          surveyResponse: value.toString(),
          locationsUuids,
          locationGroupUuids,
        };
      }

      return null;
    })
    .filter(
      (targetingAttribute): targetingAttribute is AudienceSurveyResponseTargetingAttributeInput =>
        !!targetingAttribute,
    );
};

export const getRespondedStatus = (attribute: SurveyAttribute): MemberResponded => {
  const { surveyResponse, minAmount, maxAmount, selectedOptions } = attribute;

  const isResponded =
    surveyResponse || selectedOptions?.length || !isNil(minAmount) || !isNil(maxAmount);

  return isResponded ? 'responded' : 'not-responded';
};

export const getSliderValueFromAttribute = (attribute: SurveyAttribute): number | undefined => {
  const { surveyResponse } = attribute;

  const parsedValue = Number(surveyResponse);
  const numericValue = Number.isNaN(parsedValue) ? undefined : parsedValue;

  return numericValue;
};

export const getRespondedWithLabelToDisplay = (attribute: SurveyAttribute): string => {
  const { survey, selectedOptions } = attribute;

  if (!survey) {
    return RESPONDED_WITH_LABEL;
  }

  const surveyQuestion = getSurveyQuestion(survey);
  const isSingleOrMultipleChoiceQuestion = getIsSingleOrMultipleChoiceQuestion(surveyQuestion);

  if (isSingleOrMultipleChoiceQuestion && (selectedOptions?.length ?? 0) > 1) {
    return RESPONDED_WITH_ANY_OF_LABEL;
  }

  return RESPONDED_WITH_LABEL;
};

export const getRespondedWithValueToDisplay = (attribute: SurveyAttribute): string | null => {
  const { surveyOperator, surveyResponse, minAmount, maxAmount, selectedOptions, survey } =
    attribute;

  if (!survey) {
    return null;
  }

  const surveyQuestion = getSurveyQuestion(survey);

  if (getIsSingleOrMultipleChoiceQuestion(surveyQuestion) && selectedOptions) {
    return selectedOptions
      .map((optionUuid) => {
        const option = surveyQuestion.options.find((option) => option.uuid === optionUuid);
        return option?.label;
      })
      .filter((option) => !!option)
      .join(', ');
  }

  if (!getIsSliderQuestion(surveyQuestion)) {
    return null;
  }

  const sliderOperator = sliderOperatorByInputOperator[surveyOperator];

  if (!sliderOperator) {
    return null;
  }

  if (sliderOperator === 'is-between' && !isNil(minAmount) && !isNil(maxAmount)) {
    return `${minAmount}-${maxAmount}`;
  }

  if (sliderOperator !== 'is-between' && !isNil(surveyResponse)) {
    return `${titleBySliderOperator[sliderOperator]} ${surveyResponse}`;
  }

  return null;
};
